import { baseOf } from './cash';

export type SpotPerformanceTrade = {
  amount: number;
  price?: number;
  market_value?: number;
  type: string;
  createdAt?: string;
};

export type TradePerformance = {
  totalCoins: number;
  totalCost: number;
  avgPrice: number;
  currentValue: number;
  plUSDT: number;
  plPercent: number;
};

const toFiniteNumber = (value: number | undefined): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : 0
);

const getUnitPrice = (trade: SpotPerformanceTrade): number => {
  const marketValue = toFiniteNumber(trade.market_value);
  if (marketValue > 0) return marketValue;

  return toFiniteNumber(trade.price);
};

const getCreatedAtMs = (trade: SpotPerformanceTrade): number => {
  if (!trade.createdAt) return 0;
  const time = new Date(trade.createdAt).getTime();
  return Number.isNaN(time) ? 0 : time;
};

export const calculateSpotTradePerformance = (
  trades: SpotPerformanceTrade[] | null | undefined,
  selectedCoinPrice: number,
): TradePerformance => {
  let totalCoins = 0;
  let totalCost = 0;

  const sortedTrades = [...(trades ?? [])].sort(
    (a, b) => getCreatedAtMs(a) - getCreatedAtMs(b),
  );

  sortedTrades.forEach((trade) => {
    const amount = toFiniteNumber(trade.amount);
    const unitPrice = getUnitPrice(trade);
    if (amount <= 0 || unitPrice <= 0) return;

    if (trade.type === 'buy') {
      totalCoins += amount;
      totalCost += amount * unitPrice;
      return;
    }

    if (trade.type === 'sell' && totalCoins > 0) {
      const soldAmount = Math.min(amount, totalCoins);
      const avgCost = totalCost / totalCoins;
      totalCost -= soldAmount * avgCost;
      totalCoins -= soldAmount;
      if (totalCoins <= 1e-10) {
        totalCoins = 0;
        totalCost = 0;
      }
    }
  });

  const currentPrice = toFiniteNumber(selectedCoinPrice);
  const currentValue = totalCoins * currentPrice;
  const avgPrice = totalCoins > 0 ? totalCost / totalCoins : 0;
  const plUSDT = currentValue - totalCost;
  const plPercent = totalCost > 0 ? (plUSDT / totalCost) * 100 : 0;

  return { totalCoins, totalCost, avgPrice, currentValue, plUSDT, plPercent };
};

export type SpotHistoryTrade = SpotPerformanceTrade & { trading_pair: string };

type CoinPrices = Record<string, { last?: number } | undefined>;

// Performance per base coin, keyed as the trade pair spells the base (`BTC`).
// Grouping by base rather than by pair keeps one position when the history
// mixes cash currencies (BTC/USDT before the switch, BTC/USDC after).
// A coin without a current price is left out.
export const spotPerformanceByBase = (
  trades: SpotHistoryTrade[] | null | undefined,
  prices: CoinPrices | null | undefined,
): Record<string, TradePerformance> => {
  const groups: Record<string, SpotHistoryTrade[]> = {};
  (trades ?? []).forEach((trade) => {
    const base = baseOf(trade.trading_pair);
    if (!base) return;
    if (!groups[base]) groups[base] = [];
    groups[base].push(trade);
  });

  const result: Record<string, TradePerformance> = {};
  Object.entries(groups).forEach(([base, baseTrades]) => {
    const price = prices?.[base.toLowerCase()];
    if (price) {
      result[base] = calculateSpotTradePerformance(baseTrades, price.last);
    }
  });
  return result;
};
